/**
 * SidebarFooter — fiscal period indicator, ledger selector, and footer wrapper.
 */

import React, { useState, useEffect } from 'react'

import { Badge } from '@elsapiens/ui'
import { NavLink } from 'react-router-dom'

import { getCurrentPeriod, type FiscalPeriod } from '../../services/fiscal-period-service'
import { useLedgerContext } from '../../stores/ledger-store'
import { useOrganization } from '../../stores/organization-store'
import { Select } from '../ui'

interface StatusConfig {
  badge: string
  variant: 'success' | 'warning' | 'error' | 'secondary'
}

type StatusMap = Record<string, StatusConfig>

export function getStatusConfig(): StatusMap {
  return {
    PERIOD_STATUS_OPEN: { badge: 'OPEN', variant: 'success' },
    PERIOD_STATUS_CLOSED: { badge: 'CLOSED', variant: 'warning' },
    PERIOD_STATUS_LOCKED: { badge: 'LOCKED', variant: 'error' },
  }
}

export function useFiscalPeriod(organizationId: string | null): FiscalPeriod | null {
  const [currentPeriod, setCurrentPeriod] = useState<FiscalPeriod | null>(null)

  useEffect(() => {
    if (!organizationId) { return }

    async function loadCurrentPeriod(): Promise<void> {
      try {
        if (!organizationId) { return }
        const response = await getCurrentPeriod(organizationId)
        if (response.data) {
          setCurrentPeriod(response.data)
        }
      } catch {
        // silently ignore
      }
    }

    void loadCurrentPeriod()
  }, [organizationId])

  return currentPeriod
}

export function PeriodIndicator(): React.JSX.Element | null {
  const { organizationId } = useOrganization()
  const currentPeriod = useFiscalPeriod(organizationId)

  if (!currentPeriod) {
    return null
  }

  const statusConfig = getStatusConfig()
  const status = currentPeriod.status
    ? (statusConfig[currentPeriod.status as keyof typeof statusConfig] ?? statusConfig.PERIOD_STATUS_OPEN)
    : statusConfig.PERIOD_STATUS_OPEN

  return (
    <NavLink
      className="block rounded-md hover:bg-muted transition-colors el-p-sm"
      to="/admin/periods"
    >
      <div className="flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <div className="text-xs font-medium text-muted-foreground mb-0.5">
            Current Period
          </div>
          <div className="text-sm font-medium text-foreground truncate">
            {currentPeriod.name}
          </div>
        </div>
        <div className="ml-2 flex-shrink-0">
          <Badge size="sm" variant={status?.variant}>
            {status?.badge}
          </Badge>
        </div>
      </div>
    </NavLink>
  )
}

export function LedgerSelector(): React.JSX.Element | null {
  const { ledgers, currentLedger, selectLedger, isLoading } = useLedgerContext()

  if (isLoading || ledgers.length <= 1) {
    return null
  }

  const handleLedgerChange = (ledgerId: string): void => {
    void selectLedger(ledgerId)
  }

  return (
    <div>
      <label className="block text-xs font-medium text-muted-foreground mb-1" htmlFor="ledger-select">
        Ledger
      </label>
      <Select
        hideMessage
        id="ledger-select"
        options={ledgers.map((ledger) => ({ value: ledger.id, label: ledger.name }))}
        size="sm"
        value={currentLedger?.id ?? ''}
        onChange={(value) => handleLedgerChange(value)}
      />
    </div>
  )
}

const isMac = typeof navigator !== 'undefined' && /mac/i.test(navigator.platform)
const altSymbol = isMac ? '⌥' : 'Alt+'

export function AdminLink(): React.JSX.Element {
  return (
    <NavLink
      className={({ isActive }) =>
        `flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors ${
          isActive
            ? 'bg-primary/10 text-primary font-medium'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
        }`
      }
      to="/admin"
    >
      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
        <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
      </svg>
      <span className="flex-1">Administration</span>
      <kbd className="hidden lg:inline-flex items-center text-[10px] font-mono text-muted-foreground/50 bg-muted/40 border border-border/40 rounded px-1 py-0.5 leading-none select-none">
        {altSymbol}M
      </kbd>
    </NavLink>
  )
}

export function SidebarFooterContent(): React.JSX.Element {
  return (
    <div className="el-space-y-sm">
      <AdminLink />
      <LedgerSelector />
      <PeriodIndicator />
    </div>
  )
}
